/** Most archives the server takes in one upload. */
export const MAX_ZIPS_PER_UPLOAD = 10;

/**
 * Most files one level of a dropped folder is read for. Reading a folder costs one
 * call per file, so a folder of tens of thousands of files would hold the tab for as
 * long as it took to walk them. This is not a server rule: one shapefile is four or
 * five files, so 500 is a hundred shapefiles, and the 500 MB an upload is limited to
 * runs out well before a real export reaches this count.
 */
export const MAX_FOLDER_FILES = 500;

/**
 * Most files one drop is read for, counting every folder in it and the loose files
 * beside them. The cap above bounds one folder and nothing bounds ten of them, and
 * every file goes up as a chunked request of its own, so the count is held here as
 * well. This is the file count of the largest drop the server already takes as
 * archives: ten zips of twenty-five shapefiles is 250 shapefiles, or about 1000 files.
 */
export const MAX_DROP_FILES = 1000;

/** Shown when a drop holds more archives than the server takes. */
export const TOO_MANY_ZIPS_MESSAGE = `You can upload up to ${MAX_ZIPS_PER_UPLOAD} zip files at a time.`;

export function isZip(name: string): boolean {
  return name.toLowerCase().endsWith(".zip");
}

/**
 * Identifies a dropped file across renders: it is the row key, the uid antd holds
 * for the same file, and the key an archive is read under.
 */
export function fileKeyOf(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

export function countZips(items: { name: string }[]): number {
  return items.filter((item) => isZip(item.name)).length;
}

/**
 * Keeps the archives the server takes and every file that is not an archive. The
 * archives are kept in the order they were dropped, so the list stays predictable.
 */
export function trimExtraZips<T extends { name: string }>(items: T[]): T[] {
  let zips = 0;
  return items.filter((item) => {
    if (!isZip(item.name)) return true;
    zips += 1;
    return zips <= MAX_ZIPS_PER_UPLOAD;
  });
}

/** Most bytes the server takes in one upload, counting every file in the drop. */
export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;

/** Shown when a drop was cut down to fit that total. */
export const TOO_MANY_BYTES_MESSAGE = `One upload is limited to 500 MB in total. The files past that were not added.`;

/** Shown when a whole drop is over that total, so none of it is sent. */
export const UPLOAD_TOO_LARGE_MESSAGE = `One upload is limited to 500 MB in total.`;

/**
 * Keeps the files that fit inside one upload, in the order they were dropped. A
 * file that would take the total past the limit is left out, and a smaller file
 * after it is still kept, so the drop fills the budget it has.
 */
export function trimToByteBudget<T extends { size?: number }>(items: T[]): T[] {
  let total = 0;
  return items.filter((item) => {
    const size = item.size ?? 0;
    if (total + size > MAX_UPLOAD_BYTES) return false;
    total += size;
    return true;
  });
}

/**
 * The header of the issues panel in the drop modal: how many drops were taken out
 * of the list, and how many of the ones left have something worth reading. The noun
 * stays "zip file" while the panel only holds archives, and widens to "file" as soon
 * as a folder or a loose file has something to answer for.
 */
export function zipPanelHeader(
  ignored: number,
  issues: number,
  noun = "zip file"
): string {
  const name = (count: number) => (count === 1 ? noun : `${noun}s`);
  const parts: string[] = [];
  if (ignored > 0) {
    parts.push(`${ignored} ${name(ignored)} will be ignored`);
  }
  if (issues > 0) {
    parts.push(`${issues} ${name(issues)} ${issues === 1 ? "has" : "have"} issues`);
  }
  return parts.join(" and ");
}
